import { memo, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import Button, {
	BUTTON_ICON_ALIGNMENTS,
	BUTTON_SIZES,
	BUTTON_VARIANTS,
} from "app/pages/.shared/form/Button";
import IconRight from "app/pages/.shared/static/icons/IconRight";
import { sendTagOnGoToPaymentPageClick } from "app/utils/analytics";
import QuotationPriceSummary from "app/pages/.shared/PriceSummary/QuotationPriceSummary";
import useOnScreen from "app/pages/.shared/useOnScreen";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import PaymentDescriptionTermsContainer from "app/pages/Booking/Payment/PaymentDescriptionTerms/PaymentDescriptionTermsContainer";
import PayNowNote from "app/pages/.shared/PayNow/PayNowNote";
import MilesEarnedContainer from "app/pages/.shared/MilesEarned/MilesEarnedContainer";
import "./QuotationSubmit.scss";

const QuotationSubmit = ({
	isLoading = false,
	submitting = false,
	isHotelOnly = false,
	enableFlexRateChoice = false,
	onShowFlexRateChoice = () => {},
	availablePaymentMethods = [],
	depositPercentage,
	hasChildren,
	total,
	pricePerPerson,
	isBookingEligibleForDeposit,
}) => {
	const { track } = useAnalytics();
	const ref = useRef();
	const isElementOnScreen = useOnScreen({ ref, triggerOnce: true });

	const payNowNote = total > 0 && (
		<PayNowNote
			total={total}
			depositPercentage={depositPercentage}
			availablePaymentMethods={availablePaymentMethods}
			displayAmountSuffix={false}
		/>
	);

	let paymentDescriptionTermsContent;

	if (isBookingEligibleForDeposit) {
		paymentDescriptionTermsContent = payNowNote;
	} else {
		paymentDescriptionTermsContent = <PaymentDescriptionTermsContainer />;
	}

	useEffect(() => {
		if (isElementOnScreen && !isLoading) {
			track("quote_view_CTA");
		}
	}, [isElementOnScreen, isLoading]);

	const handleOnClick = useCallback(() => {
		if (enableFlexRateChoice) {
			onShowFlexRateChoice();
		}
		sendTagOnGoToPaymentPageClick();
	}, [enableFlexRateChoice]);

	return (
		<div className="quotation-submit" ref={ref}>
			{isLoading ? (
				<div className="quotation-submit__button">
					<Button
						variant={BUTTON_VARIANTS.FILLED}
						size={BUTTON_SIZES.LARGE}
						disabled={true}
						loading={true}
						submit
						data-testid="quote-submit-button"
						label={<FormattedMessage id="general.button.loading.label" />}
					/>
				</div>
			) : (
				<>
					<QuotationPriceSummary
						total={total}
						hasChildren={hasChildren}
						isHotelOnly={isHotelOnly}
					/>

					<div className="quotation-submit__button">
						<Button
							id="quotation-cta"
							variant={BUTTON_VARIANTS.FILLED}
							size={BUTTON_SIZES.LARGE}
							alignIcon={BUTTON_ICON_ALIGNMENTS.RIGHT}
							disabled={submitting || isLoading}
							loading={submitting || isLoading}
							submit={!enableFlexRateChoice}
							data-testid="quote-submit-button"
							onClick={handleOnClick}
							label={<FormattedMessage id="quotation.button.cta" />}
							icon={<IconRight />}
						/>
					</div>

					<MilesEarnedContainer
						price={pricePerPerson}
						prefixMessageId="miles.earned.search.engine.prefix"
						suffixLabel={<FormattedMessage id="miles.earned.suffix" />}
					/>

					{paymentDescriptionTermsContent}
				</>
			)}
		</div>
	);
};

QuotationSubmit.propTypes = {
	isHotelOnly: PropTypes.bool,
	submitting: PropTypes.bool,
	isLoading: PropTypes.bool,
	enableFlexRateChoice: PropTypes.bool,
	onShowFlexRateChoice: PropTypes.func,
	depositPercentage: PropTypes.number,
	availablePaymentMethods: PropTypes.array,
	hasChildren: PropTypes.bool,
	total: PropTypes.number,
	pricePerPerson: PropTypes.number,
	isBookingEligibleForDeposit: PropTypes.bool,
};

export default memo(QuotationSubmit);
